import type { ReactNode } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'

import type { Whoami } from './auth'
import { whoCan } from './i18n/explain'
import { useT } from './i18n/LocaleProvider'

export interface RouteGuardProps {
  inside: boolean | null
  who: Whoami | null
  capability: string | null
  safeTo: string | null
  children: ReactNode
}

export function RouteGuard({
  inside,
  who,
  capability,
  safeTo,
  children,
}: RouteGuardProps): JSX.Element {
  const location = useLocation()
  const t = useT()

  if (inside === null) {
    return (
      <main>
        <p role="status">{t('guard.opening')}</p>
      </main>
    )
  }

  if (!inside || who === null) {
    const returnTo = `${location.pathname}${location.search}`
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />
  }

  if (who.must_change_password && location.pathname !== '/password/change') {
    return <Navigate to="/password/change" replace />
  }

  if (!who.must_change_password && location.pathname === '/password/change') {
    return <Navigate to={safeTo ?? '/login'} replace />
  }

  if (capability !== null && !who.may.includes(capability)) {
    return (
      <main>
        <h1>{t('guard.denied')}</h1>
        {/* Chi puo' aprirla, non il nome interno del permesso: chi legge non
            ottiene un permesso, lo chiede a qualcuno. Il codice resta accanto
            per chi deve capire. */}
        <p>
          {t('guard.requires', { who: whoCan(t, capability) })}{' '}
          <code className="tenue">{capability}</code>
        </p>
        <p>
          {safeTo === null ? (
            t('guard.noSafe')
          ) : (
            <Link to={safeTo}>{t('guard.openSafe')}</Link>
          )}
        </p>
      </main>
    )
  }

  return <>{children}</>
}
